/**
 * Fase 3 — Catálogo da vitrine por seção (leve).
 * Ao abrir: Destaques (limit 10) + uma seção (limit 20) ou "Todos" se não houver seções.
 * Sem auto-paginar o catálogo inteiro.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import type { Product } from '../types';
import { compareDepartments } from '../utils/catalogOrder';

export const STORE_DESTAQUES_LIMIT = 10;
export const STORE_SECTION_PAGE_SIZE = 20;
export const STORE_TODOS_LABEL = 'Todos';

function mapDocs(docs: QueryDocumentSnapshot<DocumentData>[]): Product[] {
  return docs.map((d) => ({ id: d.id, ...d.data() } as Product));
}

export function resolveStoreSections(input: {
  departments?: string[] | null;
  tags?: string[] | null;
  customCategories?: string[] | null;
  ordemDepartamentos?: Record<string, number> | null;
}): string[] {
  const fromLists = [
    ...(input.departments || []),
    ...(input.tags || []),
    ...(input.customCategories || []),
  ]
    .map((s) => String(s || '').trim())
    .filter(Boolean);

  const fromOrdem = Object.keys(input.ordemDepartamentos || {}).map((s) => s.trim()).filter(Boolean);
  const set = new Set<string>([...fromLists, ...fromOrdem]);
  return Array.from(set).sort((a, b) =>
    compareDepartments(a, b, input.ordemDepartamentos || undefined)
  );
}

export interface UseStoreSectionCatalogResult {
  sections: string[];
  activeSection: string;
  setActiveSection: (section: string) => void;
  destaques: Product[];
  sectionProducts: Product[];
  /** Destaques + seção ativa (para busca em memória / carrinho local) */
  products: Product[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  mode: 'section' | 'todos';
  /** Leituras aproximadas nesta sessão (páginas + destaques) */
  sessionReads: number;
}

export function useStoreSectionCatalog(
  storeId: string | null,
  sectionNames: string[]
): UseStoreSectionCatalogResult {
  const namedSections = useMemo(
    () => sectionNames.map((s) => s.trim()).filter(Boolean),
    [sectionNames]
  );
  /** Sempre inclui "Todos" para não esconder produtos se a lista de seções estiver incompleta. */
  const sections = useMemo(
    () =>
      namedSections.length > 0
        ? [STORE_TODOS_LABEL, ...namedSections.filter((s) => s !== STORE_TODOS_LABEL)]
        : [STORE_TODOS_LABEL],
    [namedSections]
  );
  const mode: 'section' | 'todos' = namedSections.length > 0 ? 'section' : 'todos';

  const [activeSection, setActiveSectionState] = useState<string>(STORE_TODOS_LABEL);
  const [destaques, setDestaques] = useState<Product[]>([]);
  const [sectionProducts, setSectionProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sessionReads, setSessionReads] = useState(0);

  const lastDocRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const loadingMoreRef = useRef(false);

  const setActiveSection = useCallback((section: string) => {
    setActiveSectionState(section);
  }, []);

  // Nova loja → começa em "Todos" (leve + completo); seções nomeadas sob demanda
  useEffect(() => {
    setActiveSectionState(STORE_TODOS_LABEL);
  }, [storeId]);

  useEffect(() => {
    if (!sections.includes(activeSection)) {
      setActiveSectionState(STORE_TODOS_LABEL);
    }
  }, [sections, activeSection]);

  // Destaques (1 query, limit 10)
  useEffect(() => {
    if (!storeId) {
      setDestaques([]);
      return;
    }
    let cancelled = false;
    const q = query(
      collection(db, 'products'),
      where('vendorId', '==', storeId),
      where('ativo', '==', true),
      where('destaque', '==', true),
      orderBy('ordem', 'asc'),
      limit(STORE_DESTAQUES_LIMIT)
    );
    getDocs(q)
      .then((snap) => {
        if (cancelled) return;
        setDestaques(mapDocs(snap.docs));
        setSessionReads((n) => n + 1);
      })
      .catch((err) => {
        console.error('[useStoreSectionCatalog] destaques', err);
        if (!cancelled) setDestaques([]);
      });
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  // Seção ativa ou Todos (limit 20 + loadMore manual)
  useEffect(() => {
    if (!storeId) {
      setSectionProducts([]);
      setHasMore(false);
      lastDocRef.current = null;
      return;
    }

    let cancelled = false;
    setLoading(true);
    lastDocRef.current = null;

    const section = activeSection;
    const q =
      mode === 'todos' || section === STORE_TODOS_LABEL
        ? query(
            collection(db, 'products'),
            where('vendorId', '==', storeId),
            where('ativo', '==', true),
            orderBy('ordem', 'asc'),
            orderBy('nomeLower', 'asc'),
            limit(STORE_SECTION_PAGE_SIZE)
          )
        : query(
            collection(db, 'products'),
            where('vendorId', '==', storeId),
            where('ativo', '==', true),
            where('category', '==', section),
            orderBy('ordem', 'asc'),
            orderBy('nomeLower', 'asc'),
            limit(STORE_SECTION_PAGE_SIZE)
          );

    getDocs(q)
      .then((snap) => {
        if (cancelled) return;
        const docs = snap.docs;
        lastDocRef.current = docs.length > 0 ? docs[docs.length - 1] : null;
        setSectionProducts(mapDocs(docs));
        setHasMore(docs.length >= STORE_SECTION_PAGE_SIZE);
        setSessionReads((n) => n + 1);
        setLoading(false);
      })
      .catch((err) => {
        console.error('[useStoreSectionCatalog] section', err);
        if (cancelled) return;
        setSectionProducts([]);
        setHasMore(false);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [storeId, activeSection, mode]);

  const loadMore = useCallback(async () => {
    if (!storeId || !hasMore || loadingMoreRef.current || !lastDocRef.current) return;
    loadingMoreRef.current = true;
    setLoading(true);
    try {
      const cursor = lastDocRef.current;
      const section = activeSection;
      const q =
        mode === 'todos' || section === STORE_TODOS_LABEL
          ? query(
              collection(db, 'products'),
              where('vendorId', '==', storeId),
              where('ativo', '==', true),
              orderBy('ordem', 'asc'),
              orderBy('nomeLower', 'asc'),
              startAfter(cursor),
              limit(STORE_SECTION_PAGE_SIZE)
            )
          : query(
              collection(db, 'products'),
              where('vendorId', '==', storeId),
              where('ativo', '==', true),
              where('category', '==', section),
              orderBy('ordem', 'asc'),
              orderBy('nomeLower', 'asc'),
              startAfter(cursor),
              limit(STORE_SECTION_PAGE_SIZE)
            );
      const snap = await getDocs(q);
      const docs = snap.docs;
      if (docs.length > 0) lastDocRef.current = docs[docs.length - 1];
      setSectionProducts((prev) => {
        const map = new Map(prev.map((p) => [p.id, p]));
        for (const p of mapDocs(docs)) map.set(p.id, p);
        return Array.from(map.values());
      });
      setHasMore(docs.length >= STORE_SECTION_PAGE_SIZE);
      setSessionReads((n) => n + 1);
    } catch (err) {
      console.error('[useStoreSectionCatalog] loadMore', err);
    } finally {
      loadingMoreRef.current = false;
      setLoading(false);
    }
  }, [storeId, hasMore, activeSection, mode]);

  const products = useMemo(() => {
    const map = new Map<string, Product>();
    for (const p of destaques) map.set(p.id, p);
    for (const p of sectionProducts) map.set(p.id, p);
    return Array.from(map.values());
  }, [destaques, sectionProducts]);

  return {
    sections: mode === 'todos' ? [STORE_TODOS_LABEL] : sections,
    activeSection,
    setActiveSection,
    destaques,
    sectionProducts,
    products,
    loading,
    hasMore,
    loadMore,
    mode,
    sessionReads,
  };
}
